import type { ServiceRole } from '@andresot/protocol';
import { getAuthorizationGeneration } from '$api/client';
import type {
	AdminAuditEntry,
	AdminBenefitOperation,
	AdminBenefitResult,
	AdminBroadcast,
	AdminBroadcastCreate,
	AdminCallSummary,
	AdminPage,
	AdminPayment,
	AdminOverview,
	AdminPaymentSettingsInput,
	AdminPlatformSettingsInput,
	AdminRange,
	AdminReport,
	AdminPublicChannel,
	AdminSecurityEvent,
	AdminServerSnapshot,
	AdminSupportTicket,
	AdminSupportTicketUpdate,
	AdminTicketsQuery,
	AdminUser,
	AdminUserDetail,
	AdminUserQuery,
	AdminWorkspace
} from '@andresot/protocol/admin';
import type {
	AdminPremiumAdjustInput,
	AdminStarsAdjustInput,
	AdminStarsBulkOutcome
} from '@andresot/protocol/admin';
import * as api from '$api/admin';

export type AdminPagedSection =
	'payments' | 'channels' | 'calls' | 'support' | 'broadcasts' | 'security' | 'audit';

export interface AdminPageState<T> {
	items: T[];
	total: number;
	nextCursor?: string;
	loading: boolean;
	error: string | null;
	q: string;
	status: string;
	loaded: boolean;
}

export interface AdminTicketsPageState extends AdminPageState<AdminSupportTicket> {
	queue: AdminTicketsQuery['queue'];
}

function emptyPage<T>(): AdminPageState<T> {
	return {
		items: [],
		total: 0,
		loading: false,
		error: null,
		q: '',
		status: '',
		loaded: false
	};
}

const EMPTY_STATS: AdminOverview = {
	users: 0,
	activeToday: 0,
	onlineNow: 0,
	registrationsToday: 0,
	chats: 0,
	messagesToday: 0,
	callsNow: 0,
	blocked: 0,
	newReports: 0,
	activePremium: 0,
	revenueTodayKopecks: 0,
	storageBytes: 0,
	grantedStars: 0,
	manualPremiumOperations: 0
};

class AdminState {
	stats = $state<AdminOverview>({ ...EMPTY_STATS });
	users = $state<AdminUser[]>([]);
	reports = $state<AdminReport[]>([]);
	reportsLoading = $state(false);
	reportsError = $state<string | null>(null);
	audit = $state<AdminAuditEntry[]>([]);
	auditIntegrity = $state(true);
	paymentsPage = $state<AdminPageState<AdminPayment>>(emptyPage());
	channelsPage = $state<AdminPageState<AdminPublicChannel>>(emptyPage());
	callsPage = $state<AdminPageState<AdminCallSummary>>(emptyPage());
	ticketsPage = $state<AdminTicketsPageState>({ ...emptyPage<AdminSupportTicket>(), queue: 'all' });
	broadcastsPage = $state<AdminPageState<AdminBroadcast>>(emptyPage());
	securityPage = $state<AdminPageState<AdminSecurityEvent>>(emptyPage());
	auditPage = $state<AdminPageState<AdminAuditEntry>>(emptyPage());
	workspace = $state<AdminWorkspace | null>(null);
	servers = $state<AdminServerSnapshot[]>([]);
	systemRefreshing = $state(false);
	range = $state<AdminRange>('7d');
	loading = $state(false);
	error = $state<string | null>(null);
	usersLoading = $state(false);
	usersError = $state<string | null>(null);
	usersNextCursor = $state<string | undefined>(undefined);
	usersTotal = $state(0);
	usersQuery = $state('');
	usersRole = $state<ServiceRole | ''>('');
	usersBlocked = $state<'' | 'true' | 'false'>('');
	usersPremium = $state<'' | 'true' | 'false'>('');
	usersVerified = $state<'' | 'true' | 'false'>('');
	usersActivity = $state<'' | 'active30d' | 'inactive30d'>('');
	selectedUserIds = $state<string[]>([]);
	usersCurrentPageIds = $state<string[]>([]);
	usersSort = $state<AdminUserQuery['sort']>('created_desc');
	userDetails = $state<Record<string, AdminUserDetail>>({});
	userDetailLoading = $state(false);
	userDetailError = $state<string | null>(null);
	benefitHistory = $state<Record<string, AdminBenefitOperation[]>>({});
	benefitOperations = $state<AdminBenefitOperation[]>([]);
	benefitsLoading = $state(false);
	private loadedRole: ServiceRole | null = null;
	private usersRequest = 0;
	private userDetailRequest = 0;
	private reportsLoaded = false;
	private sectionLoadedKeys: Partial<Record<AdminPagedSection, string>> = {};
	private sectionLoadingKeys: Partial<Record<AdminPagedSection, string>> = {};
	private sectionRequests: Record<AdminPagedSection, number> = {
		payments: 0,
		channels: 0,
		calls: 0,
		support: 0,
		broadcasts: 0,
		security: 0,
		audit: 0
	};

	newReports = $derived(this.reports.filter((report) => report.status === 'new').length);

	toggleSelectedUser(id: string): void {
		this.selectedUserIds = this.selectedUserIds.includes(id)
			? this.selectedUserIds.filter((i) => i !== id)
			: this.selectedUserIds.length < 100
				? [...this.selectedUserIds, id]
				: this.selectedUserIds;
	}
	selectVisibleUsers(): void {
		this.selectedUserIds = [
			...new Set([...this.selectedUserIds, ...this.usersCurrentPageIds])
		].slice(0, 100);
	}
	reset(): void {
		this.selectedUserIds = [];
		this.usersCurrentPageIds = [];
		this.usersActivity = '';
		this.stats = { ...EMPTY_STATS };
		this.workspace = null;
		this.users = [];
		this.reports = [];
		this.audit = [];
		this.servers = [];
		this.userDetails = {};
		this.benefitHistory = {};
		this.benefitOperations = [];
		this.paymentsPage = emptyPage();
		this.channelsPage = emptyPage();
		this.callsPage = emptyPage();
		this.ticketsPage = { ...emptyPage<AdminSupportTicket>(), queue: 'all' };
		this.broadcastsPage = emptyPage();
		this.securityPage = emptyPage();
		this.auditPage = emptyPage();
		this.loadedRole = null;
		this.reportsLoaded = false;
		this.sectionLoadedKeys = {};
		this.sectionLoadingKeys = {};
		this.usersRequest += 1;
		this.userDetailRequest += 1;
		for (const section of Object.keys(this.sectionRequests) as AdminPagedSection[])
			this.sectionRequests[section] += 1;
		this.loading =
			this.usersLoading =
			this.reportsLoading =
			this.userDetailLoading =
			this.benefitsLoading =
			this.systemRefreshing =
				false;
		this.error = this.usersError = this.reportsError = this.userDetailError = null;
		this.usersNextCursor = undefined;
		this.usersTotal = 0;
		this.usersQuery =
			this.usersRole =
			this.usersBlocked =
			this.usersPremium =
			this.usersVerified =
				'';
		this.usersSort = 'created_desc';
		this.range = '7d';
		this.auditIntegrity = true;
	}

	serviceRoleOf(user: { serviceRole?: ServiceRole }): ServiceRole {
		return user.serviceRole ?? 'user';
	}

	isBlocked(userId: string): boolean {
		return this.users.find((user) => user.id === userId)?.isBlocked ?? false;
	}

	userById(userId: string): AdminUser {
		return (
			this.users.find((user) => user.id === userId) ?? {
				id: userId,
				login: 'unknown',
				name: 'Неизвестный пользователь',
				serviceRole: 'user',
				isBlocked: false,
				isOfficial: false,
				createdAt: 0,
				activeSessions: 0,
				starsBalance: 0,
				storageBytes: 0,
				reportsCount: 0
			}
		);
	}

	async load(role: ServiceRole, force = false): Promise<void> {
		if (this.loading || (!force && this.loadedRole === role)) return;
		const generation = getAuthorizationGeneration();
		this.loading = true;
		this.error = null;
		try {
			const tasks: Promise<void>[] = [];
			if (['owner', 'admin', 'moderator', 'support'].includes(role)) {
				tasks.push(
					api.fetchOverview().then((stats) => {
						if (generation === getAuthorizationGeneration()) this.stats = stats;
					})
				);
			}
			if (['owner', 'admin', 'moderator', 'support', 'auditor'].includes(role)) {
				tasks.push(
					api.fetchWorkspace(this.range).then((workspace) => {
						if (generation !== getAuthorizationGeneration()) return;
						this.workspace = workspace;
						this.stats = workspace.overview;
					})
				);
			}
			await Promise.all(tasks);
			if (generation === getAuthorizationGeneration()) this.loadedRole = role;
		} catch (error) {
			if (generation === getAuthorizationGeneration())
				this.error =
					error instanceof Error ? error.message : 'Не удалось загрузить администрирование';
		} finally {
			if (generation === getAuthorizationGeneration()) this.loading = false;
		}
	}

	async searchUsers(query: string, append = false): Promise<void> {
		const normalized = query.trim();
		if (append && !this.usersNextCursor) return;
		const requestId = ++this.usersRequest;
		const queryKey = JSON.stringify({
			q: normalized,
			role: this.usersRole,
			blocked: this.usersBlocked,
			premium: this.usersPremium,
			verified: this.usersVerified,
			activity: this.usersActivity,
			sort: this.usersSort
		});
		this.usersLoading = true;
		this.usersError = null;
		try {
			const page = await api.fetchUsers({
				q: normalized,
				role: this.usersRole || undefined,
				blocked: this.usersBlocked ? this.usersBlocked === 'true' : undefined,
				premium: this.usersPremium ? this.usersPremium === 'true' : undefined,
				verified: this.usersVerified ? this.usersVerified === 'true' : undefined,
				activity: this.usersActivity || undefined,
				sort: this.usersSort,
				cursor: append && queryKey === this.usersQuery ? this.usersNextCursor : undefined,
				limit: 25
			});
			if (requestId !== this.usersRequest) return;
			this.users =
				append && queryKey === this.usersQuery ? [...this.users, ...page.users] : page.users;
			this.usersTotal = page.total;
			this.usersCurrentPageIds = page.users.map((user) => user.id);
			this.usersNextCursor = page.nextCursor;
			this.usersQuery = queryKey;
		} catch (error) {
			if (requestId !== this.usersRequest) return;
			this.usersError =
				error instanceof Error ? error.message : 'Не удалось загрузить пользователей';
		} finally {
			if (requestId === this.usersRequest) this.usersLoading = false;
		}
	}

	async loadUserDetail(userId: string, force = false): Promise<void> {
		if (!force && this.userDetails[userId]) return;
		const requestId = ++this.userDetailRequest;
		this.userDetailLoading = true;
		this.userDetailError = null;
		try {
			const user = await api.fetchUserDetail(userId);
			if (requestId !== this.userDetailRequest) return;
			this.userDetails[userId] = user;
			this.replaceUser(user);
		} catch (error) {
			if (requestId !== this.userDetailRequest) return;
			this.userDetailError =
				error instanceof Error ? error.message : 'Не удалось загрузить карточку пользователя';
		} finally {
			if (requestId === this.userDetailRequest) this.userDetailLoading = false;
		}
	}

	async updateUserProfile(userId: string, input: Parameters<typeof api.updateUserProfile>[1]) {
		const user = await api.updateUserProfile(userId, input);
		this.userDetails[userId] = user;
		this.replaceUser(user);
		return user;
	}

	async resendUserVerification(userId: string): Promise<void> {
		await api.resendUserVerification(userId);
		await this.loadUserDetail(userId, true);
	}

	async retryUserMailbox(userId: string): Promise<void> {
		await api.retryUserMailbox(userId);
		await this.loadUserDetail(userId, true);
	}

	async loadReports(force = false): Promise<void> {
		if (this.reportsLoading || (this.reportsLoaded && !force)) return;
		this.reportsLoading = true;
		this.reportsError = null;
		try {
			this.reports = await api.fetchReports();
			this.reportsLoaded = true;
		} catch (error) {
			this.reportsError = error instanceof Error ? error.message : 'Не удалось загрузить жалобы';
		} finally {
			this.reportsLoading = false;
		}
	}

	pageFor(section: AdminPagedSection): AdminPageState<unknown> {
		switch (section) {
			case 'payments':
				return this.paymentsPage;
			case 'channels':
				return this.channelsPage;
			case 'calls':
				return this.callsPage;
			case 'support':
				return this.ticketsPage;
			case 'broadcasts':
				return this.broadcastsPage;
			case 'security':
				return this.securityPage;
			case 'audit':
				return this.auditPage;
		}
	}

	private pageKey(section: AdminPagedSection, state: AdminPageState<unknown>): string {
		return JSON.stringify({
			q: state.q.trim(),
			status: state.status,
			...(section === 'support' ? { queue: this.ticketsPage.queue } : {})
		});
	}

	private async loadPage<T>(
		section: AdminPagedSection,
		state: AdminPageState<T>,
		fetcher: (query: {
			q: string;
			queue?: AdminTicketsQuery['queue'];
			status?: never;
			cursor?: string;
			limit: number;
		}) => Promise<AdminPage<T>>,
		append: boolean,
		force: boolean
	): Promise<void> {
		const key = this.pageKey(section, state);
		const generation = getAuthorizationGeneration();
		if (state.loading && this.sectionLoadingKeys[section] === key && !force) return;
		const samePage = this.sectionLoadedKeys[section] === key;
		if (!append && state.loaded && samePage && !force) return;
		append = append && samePage;
		if (append && !state.nextCursor) return;
		const requestId = ++this.sectionRequests[section];
		this.sectionLoadingKeys[section] = key;
		state.loading = true;
		state.error = null;
		try {
			const page = await fetcher({
				q: state.q.trim(),
				...(section === 'support' ? { queue: this.ticketsPage.queue } : {}),
				...(state.status ? { status: state.status as never } : {}),
				...(append && state.nextCursor ? { cursor: state.nextCursor } : {}),
				limit: 25
			});
			if (
				generation !== getAuthorizationGeneration() ||
				requestId !== this.sectionRequests[section] ||
				key !== this.pageKey(section, state)
			)
				return;
			state.items = append ? [...state.items, ...page.items] : page.items;
			state.total = page.total;
			state.nextCursor = page.nextCursor;
			state.loaded = true;
			this.sectionLoadedKeys[section] = key;
		} catch (error) {
			if (
				generation !== getAuthorizationGeneration() ||
				requestId !== this.sectionRequests[section] ||
				key !== this.pageKey(section, state)
			)
				return;
			state.error = error instanceof Error ? error.message : 'Не удалось загрузить список';
		} finally {
			if (
				generation === getAuthorizationGeneration() &&
				requestId === this.sectionRequests[section]
			)
				state.loading = false;
		}
	}

	async loadSection(section: AdminPagedSection, append = false, force = false): Promise<void> {
		switch (section) {
			case 'payments':
				return this.loadPage(section, this.paymentsPage, api.fetchPaymentsPage, append, force);
			case 'channels':
				return this.loadPage(section, this.channelsPage, api.fetchChannelsPage, append, force);
			case 'calls':
				return this.loadPage(section, this.callsPage, api.fetchCallsPage, append, force);
			case 'support':
				return this.loadPage(section, this.ticketsPage, api.fetchTicketsPage, append, force);
			case 'broadcasts':
				return this.loadPage(section, this.broadcastsPage, api.fetchBroadcastsPage, append, force);
			case 'security':
				return this.loadPage(section, this.securityPage, api.fetchSecurityPage, append, force);
			case 'audit': {
				const result = await this.loadAuditPage(append, force);
				return result;
			}
		}
	}

	private async loadAuditPage(append: boolean, force: boolean): Promise<void> {
		const state = this.auditPage;
		const key = this.pageKey('audit', state);
		const generation = getAuthorizationGeneration();
		if (state.loading && this.sectionLoadingKeys.audit === key && !force) return;
		const samePage = this.sectionLoadedKeys.audit === key;
		if (!append && state.loaded && samePage && !force) return;
		append = append && samePage;
		if (append && !state.nextCursor) return;
		const requestId = ++this.sectionRequests.audit;
		this.sectionLoadingKeys.audit = key;
		state.loading = true;
		state.error = null;
		try {
			const result = await api.fetchAuditPage({
				q: state.q.trim(),
				...(append && state.nextCursor ? { cursor: state.nextCursor } : {}),
				limit: 25
			});
			if (
				generation !== getAuthorizationGeneration() ||
				requestId !== this.sectionRequests.audit ||
				key !== this.pageKey('audit', state)
			)
				return;
			state.items = append ? [...state.items, ...result.page.items] : result.page.items;
			state.total = result.page.total;
			state.nextCursor = result.page.nextCursor;
			state.loaded = true;
			this.sectionLoadedKeys.audit = key;
			this.audit = state.items;
			this.auditIntegrity = result.integrity;
		} catch (error) {
			if (
				generation !== getAuthorizationGeneration() ||
				requestId !== this.sectionRequests.audit ||
				key !== this.pageKey('audit', state)
			)
				return;
			state.error = error instanceof Error ? error.message : 'Не удалось загрузить аудит';
		} finally {
			if (generation === getAuthorizationGeneration() && requestId === this.sectionRequests.audit)
				state.loading = false;
		}
	}

	async setSectionFilter(section: AdminPagedSection, q: string, status = ''): Promise<void> {
		const state = this.pageFor(section);
		state.q = q;
		state.status = status;
		state.loaded = false;
		state.nextCursor = undefined;
		await this.loadSection(section, false, true);
	}

	async refreshSection(section: AdminPagedSection): Promise<void> {
		await this.loadSection(section, false, true);
	}

	/**
	 * Обновить только нагрузку сервера.
	 *
	 * Отдельно от всего рабочего стола: числа стареют за секунды, а тянуть
	 * ради них списки пользователей и платежей незачем. Отказ гасим — мигать
	 * ошибкой из-за одного неудачного опроса не за что.
	 */
	async refreshResources(): Promise<void> {
		if (!this.workspace || this.systemRefreshing) return;
		this.systemRefreshing = true;
		try {
			const snapshot = await api.fetchSystemResources();
			this.workspace.resources = snapshot.resources;
			this.servers = snapshot.servers;
		} catch {
			// Пропущенный опрос ничего не ломает: следующий придёт через 15 секунд.
		} finally {
			this.systemRefreshing = false;
		}
	}

	async setRange(range: AdminRange): Promise<void> {
		this.range = range;
		this.workspace = await api.fetchWorkspace(range);
		this.stats = this.workspace.overview;
	}

	private replaceUser(user: AdminUser): void {
		const index = this.users.findIndex((item) => item.id === user.id);
		if (index < 0) this.users = [user, ...this.users];
		else this.users[index] = user;
		if ('activity' in user) this.userDetails[user.id] = user as AdminUserDetail;
	}

	async toggleBlock(userId: string, reason?: string, mfaCode?: string): Promise<boolean> {
		const blocked = this.isBlocked(userId);
		const user = await api.setBlocked(userId, !blocked, blocked ? undefined : reason, mfaCode);
		this.replaceUser(user);
		this.stats.blocked += blocked ? -1 : 1;
		await this.reloadAudit();
		return user.isBlocked;
	}

	async setServiceRole(
		userId: string,
		role: Exclude<ServiceRole, 'owner'>,
		mfaCode?: string,
		reason?: string
	): Promise<void> {
		this.replaceUser(await api.setRole(userId, role, mfaCode, reason));
		await this.reloadAudit();
	}

	async setOfficial(userId: string, official: boolean): Promise<void> {
		await api.setOfficial(userId, official);
		const user = this.users.find((item) => item.id === userId);
		if (user) user.isOfficial = official;
		if (this.userDetails[userId]) this.userDetails[userId].isOfficial = official;
		await this.reloadAudit();
	}

	async loadBenefits(userId: string): Promise<void> {
		this.benefitsLoading = true;
		try {
			const result = await api.fetchUserBenefits(userId);
			const user = this.users.find((item) => item.id === userId);
			if (user) {
				user.starsBalance = result.benefits.starsBalance;
				user.premiumExpiresAt = result.benefits.premiumExpiresAt;
			}
			this.benefitHistory[userId] = result.operations;
		} finally {
			this.benefitsLoading = false;
		}
	}

	async loadBenefitOperations(
		query: Partial<import('@andresot/protocol/admin').AdminBenefitHistoryQuery> = {}
	): Promise<void> {
		this.benefitOperations = (await api.fetchBenefitOperations({ limit: 50, ...query })).operations;
	}

	private applyBenefitResult(userId: string, result: AdminBenefitResult): void {
		const user = this.users.find((item) => item.id === userId);
		if (!user) return;
		user.starsBalance = result.benefits.starsBalance;
		user.premiumExpiresAt = result.benefits.premiumExpiresAt;
	}

	async adjustStars(userId: string, input: AdminStarsAdjustInput, mfaCode?: string): Promise<void> {
		this.applyBenefitResult(userId, await api.adjustStars(userId, input, mfaCode));
		await Promise.all([this.loadBenefits(userId), this.reloadAudit()]);
	}

	/**
	 * Начислить звёзды сразу нескольким.
	 *
	 * Возвращает построчный итог: кому досталось, кому нет и почему. Балансы у
	 * тех, кому досталось, обновляются на месте — список не приходится
	 * перечитывать целиком ради двух изменившихся строк.
	 */
	async grantStarsBulk(
		input: { userIds: string[]; amount: number; reason: string; clientId: string },
		mfaCode?: string
	): Promise<AdminStarsBulkOutcome[]> {
		const { results } = await api.grantStarsBulk(input, mfaCode);
		for (const item of results) {
			if (!item.ok || item.balance === undefined) continue;
			const user = this.users.find((row) => row.id === item.userId);
			if (user) user.starsBalance = item.balance;
		}
		await this.reloadAudit();
		return results;
	}

	async adjustPremium(
		userId: string,
		input: Extract<AdminPremiumAdjustInput, { action: string }>,
		mfaCode?: string
	): Promise<void> {
		this.applyBenefitResult(userId, await api.adjustPremium(userId, input, mfaCode));
		await Promise.all([this.loadBenefits(userId), this.reloadAudit()]);
	}

	async updateTicket(ticketId: string, input: AdminSupportTicketUpdate): Promise<void> {
		const ticket = await api.updateSupportTicket(ticketId, input);
		if (this.workspace) {
			const index = this.workspace.tickets.findIndex((item) => item.id === ticketId);
			if (index >= 0) this.workspace.tickets[index] = ticket;
		}
		const pageIndex = this.ticketsPage.items.findIndex((item) => item.id === ticketId);
		if (pageIndex >= 0) this.ticketsPage.items[pageIndex] = ticket;
		await this.reloadAudit();
	}

	async createBroadcast(input: AdminBroadcastCreate): Promise<AdminBroadcast> {
		const generation = getAuthorizationGeneration();
		const broadcast = await api.createBroadcast(input);
		if (generation !== getAuthorizationGeneration()) return broadcast;
		if (this.workspace) this.workspace.broadcasts = [broadcast, ...this.workspace.broadcasts];
		if (this.broadcastsPage.loaded) {
			this.broadcastsPage.items = [broadcast, ...this.broadcastsPage.items];
			this.broadcastsPage.total += 1;
		}
		await this.reloadAudit();
		return broadcast;
	}

	async actOnBroadcast(id: string, action: 'send' | 'cancel', mfaCode?: string): Promise<void> {
		const generation = getAuthorizationGeneration();
		const broadcast = await api.actOnBroadcast(id, action, mfaCode);
		if (generation !== getAuthorizationGeneration()) return;
		this.replaceBroadcast(broadcast);
		await this.reloadAudit();
	}

	async updateBroadcast(id: string, input: AdminBroadcastCreate): Promise<AdminBroadcast> {
		const generation = getAuthorizationGeneration();
		const broadcast = await api.updateBroadcast(id, input);
		if (generation !== getAuthorizationGeneration()) return broadcast;
		this.replaceBroadcast(broadcast);
		await this.reloadAudit();
		return broadcast;
	}

	/** Свежие цифры одной рассылки — для карточки, пока идёт отправка. */
	async refreshBroadcast(id: string): Promise<AdminBroadcast> {
		const broadcast = await api.fetchBroadcast(id);
		this.replaceBroadcast(broadcast);
		return broadcast;
	}

	private replaceBroadcast(broadcast: AdminBroadcast): void {
		if (this.workspace) {
			const index = this.workspace.broadcasts.findIndex((item) => item.id === broadcast.id);
			if (index >= 0) this.workspace.broadcasts[index] = broadcast;
		}
		const pageIndex = this.broadcastsPage.items.findIndex((item) => item.id === broadcast.id);
		if (pageIndex >= 0) this.broadcastsPage.items[pageIndex] = broadcast;
	}

	async updateSettings(input: AdminPlatformSettingsInput, mfaCode?: string): Promise<void> {
		const settings = await api.updatePlatformSettings(input, mfaCode);
		if (this.workspace) this.workspace.settings = settings;
		await this.reloadAudit();
	}

	async updatePaymentSettings(input: AdminPaymentSettingsInput, mfaCode?: string): Promise<void> {
		const paymentSettings = await api.updatePaymentSettings(input, mfaCode);
		if (this.workspace) this.workspace.paymentSettings = paymentSettings;
		await this.reloadAudit();
	}

	async checkPaymentSettings(): Promise<{ ok: boolean; message: string }> {
		return api.checkPaymentSettings();
	}

	async beginMfaSetup(): Promise<import('@andresot/protocol/admin').AdminMfaSetup> {
		return api.beginMfaSetup();
	}

	async confirmMfa(code: string): Promise<void> {
		await api.confirmMfa(code);
		if (this.workspace) this.workspace.mfaEnabled = true;
	}

	async disableMfa(code: string): Promise<void> {
		await api.disableMfa(code);
		if (this.workspace) this.workspace.mfaEnabled = false;
	}

	async exportAudit(format: 'csv' | 'json'): Promise<void> {
		await api.exportAudit(format);
	}

	async exportBenefits(
		query: Partial<import('@andresot/protocol/admin').AdminBenefitHistoryQuery>
	): Promise<void> {
		await api.exportBenefits(query);
	}

	async revokeSessions(userId: string, reason: string, mfaCode?: string): Promise<number> {
		const revoked = await api.revokeSessions(userId, reason, mfaCode);
		const user = this.users.find((item) => item.id === userId);
		if (user) user.activeSessions = 0;
		await this.reloadAudit();
		return revoked;
	}

	async resolveReport(
		reportId: string,
		status: Exclude<AdminReport['status'], 'new'>
	): Promise<void> {
		const report = await api.updateReport(reportId, status);
		const index = this.reports.findIndex((item) => item.id === reportId);
		if (index >= 0) this.reports[index] = report;
		await this.reloadAudit();
	}

	private async reloadAudit(): Promise<void> {
		if (!['owner', 'admin', 'auditor'].includes(this.loadedRole ?? 'user')) return;
		if (this.auditPage.loaded) await this.loadAuditPage(false, true);
	}
}

export const admin = new AdminState();
